import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import {
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle
} from 'react-icons/fa';

/**
 * Alert variants using CVA
 *
 * Variants:
 * - info: Blue informational alert
 * - success: Green success alert
 * - warning: Yellow/amber warning alert
 * - error: Red error/danger alert
 */
export const alertVariants = cva(
  'flex gap-3 rounded-lg border p-4',
  {
    variants: {
      variant: {
        info:
          'border-primary-200 bg-primary-50 text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
        success:
          'border-success-100 bg-success-50 text-success-700 dark:border-success-600/40 dark:bg-success-500/15 dark:text-success-400',
        warning:
          'border-warning-100 bg-warning-50 text-warning-700 dark:border-warning-600/40 dark:bg-warning-500/15 dark:text-warning-400',
        error:
          'border-error-100 bg-error-50 text-error-700 dark:border-error-600/40 dark:bg-error-500/15 dark:text-error-400',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const alertIcons = {
  info: FaInfoCircle,
  success: FaCheckCircle,
  warning: FaExclamationTriangle,
  error: FaTimesCircle,
};

/**
 * Alert Component
 *
 * @param {Object} props
 * @param {'info'|'success'|'warning'|'error'} [props.variant='info']
 * @param {string} [props.title]
 * @param {string} [props.className]
 * @param {boolean} [props.showIcon=true]
 * @param {React.ReactNode} props.children
 */
const Alert = React.forwardRef(({
  className,
  variant = 'info',
  title,
  showIcon = true,
  children,
  ...props
}, ref) => {
  const Icon = alertIcons[variant];

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {showIcon && Icon && (
        <Icon className="h-5 w-5 flex-shrink-0" />
      )}
      <div className="flex-1">
        {title && (
          <h4 className="font-semibold mb-1">{title}</h4>
        )}
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
});

Alert.displayName = 'Alert';

/**
 * AlertTitle Component (for use with separate title element)
 */
const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn('font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

/**
 * AlertDescription Component
 */
const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
export default Alert;
